// Build temporary AWS credentials from a Cognito User Pool ID token via the
// Cognito Identity Pool, then construct a DynamoDB Document client with them.
//
// We intentionally avoid `@aws-sdk/credential-providers` because that umbrella
// package transitively imports Node-only modules (node:fs, node:crypto, etc.)
// from non-Cognito providers and currently does not bundle cleanly for the
// browser with Vite/Rollup. Implementing the Cognito Identity exchange
// directly using `@aws-sdk/client-cognito-identity` is a few extra lines and
// is browser-safe.

import {
  CognitoIdentityClient,
  GetIdCommand,
  GetCredentialsForIdentityCommand,
} from '@aws-sdk/client-cognito-identity';
import { DynamoDBClient } from '@aws-sdk/client-dynamodb';
import { DynamoDBDocumentClient } from '@aws-sdk/lib-dynamodb';
import { config } from './config.js';
import { userPoolLoginsKey, getFreshIdToken } from '../auth/auth.js';

/**
 * AWS-SDK-compatible credentials provider that exchanges a Cognito User Pool
 * ID token for temporary IAM credentials, and refreshes them automatically
 * when they expire.
 *
 * The returned provider can be passed directly to any AWS SDK v3 client's
 * `credentials` option — the SDK calls it on every request and caches based
 * on `expiration`.
 *
 * IMPORTANT: the SDK only re-invokes this provider when the cached credentials
 * expire (~1h). At that point the ID token captured at sign-in has *also*
 * expired, so reusing it would fail with "NotAuthorizedException: Token is
 * inactive". We therefore fetch a FRESH ID token (via getFreshIdToken, which
 * refreshes through the Cognito refresh token) on every invocation rather than
 * closing over the initial one. The `initialIdToken` arg is kept only as a
 * fallback for the very first call.
 */
export function makeCognitoIdentityCredentialsProvider(initialIdToken) {
  const identityClient = new CognitoIdentityClient({ region: config.region });
  const loginsKey = userPoolLoginsKey();

  let identityIdPromise = null;

  /** Current logins map using a fresh (auto-refreshed) ID token. */
  async function currentLogins() {
    const idToken = (await getFreshIdToken()) || initialIdToken;
    if (!idToken) throw new Error('No valid Cognito session');
    return { [loginsKey]: idToken };
  }

  async function getIdentityId(logins) {
    if (!identityIdPromise) {
      identityIdPromise = identityClient
        .send(
          new GetIdCommand({
            IdentityPoolId: config.identityPoolId,
            Logins: logins,
          }),
        )
        .then((r) => r.IdentityId);
    }
    return identityIdPromise;
  }

  return async () => {
    const logins = await currentLogins();
    const IdentityId = await getIdentityId(logins);
    const res = await identityClient.send(
      new GetCredentialsForIdentityCommand({ IdentityId, Logins: logins }),
    );
    const c = res.Credentials;
    if (!c) {
      throw new Error('Cognito Identity returned no credentials');
    }
    return {
      accessKeyId: c.AccessKeyId,
      secretAccessKey: c.SecretKey,
      sessionToken: c.SessionToken,
      expiration: c.Expiration,
    };
  };
}

/**
 * Build a DynamoDB document client authenticated for the given Cognito ID
 * token. The underlying credentials are short-lived; the SDK refreshes them
 * automatically when they expire.
 */
export function buildDocClient(idToken) {
  const credentials = makeCognitoIdentityCredentialsProvider(idToken);

  const raw = new DynamoDBClient({
    region: config.region,
    credentials,
    // `keepalive` lets a request that's been dispatched survive a page unload
    // (the browser keeps it in flight). This is what makes the optimistic-delete
    // toast's deferred DynamoDB write actually complete when the user navigates
    // away before the toast timer fires — the toast layer flushes pending
    // commits on pagehide, and keepalive carries them through. (Default browser
    // handler reads `keepAlive` from this config; see @smithy/fetch-http-handler.)
    requestHandler: { keepAlive: true },
  });
  return DynamoDBDocumentClient.from(raw, {
    marshallOptions: { removeUndefinedValues: true },
  });
}

/**
 * Resolve the caller's Cognito Identity ID for the given ID token. This is
 * the value used as the DynamoDB partition key for per-user data; it is
 * stable for a given (user pool, user) pair.
 *
 * IAM policies in the Identity Pool's authenticated role restrict access
 * to rows where the leading partition-key value equals the caller's
 * identity ID, so this value need not be kept secret — it is a public
 * AWS resource identifier.
 */
export async function fetchIdentityId(idToken) {
  const identityClient = new CognitoIdentityClient({ region: config.region });
  const res = await identityClient.send(
    new GetIdCommand({
      IdentityPoolId: config.identityPoolId,
      Logins: { [userPoolLoginsKey()]: idToken },
    }),
  );
  return res.IdentityId;
}
